import { useTranslation } from "react-i18next";

const getEventTypes = (t: (key: string) => string) => [
  { icon: "ri-vidicon-line", label: t("eventTypeConference") },
  { icon: "ri-team-line", label: t("eventTypeWorkshop") },
  { icon: "ri-computer-line", label: t("eventTypeWebinar") },
  { icon: "ri-links-line", label: t("eventTypeNetworking") },
];

const getFeaturedEvent = (t: (key: string) => string) => ({
  tag: t("eventTagUpcoming"),
  title: "East Africa Investment Summit 2026",
  date: "June 14–15, 2026",
  location: "Kigali Convention Centre, Rwanda",
  speakers: 24,
  attendees: 600,
  spots: 180,
});

export default function EventsHero() {
  const { t } = useTranslation();
  const eventTypes = getEventTypes(t);
  const featuredEvent = getFeaturedEvent(t);

  return (
    <section id="page-hero" data-dark-section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A1628]">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20East%20African%20business%20investment%20conference%20auditorium%20filled%20with%20diverse%20international%20audience%2C%20stage%20with%20speakers%20presenting%2C%20large%20screen%20display%2C%20dramatic%20blue%20stage%20lighting%2C%20professional%20event%20photography%2C%20wide%20shot%20of%20packed%20conference%20hall&width=1920&height=1080&seq=events-hero-v2&orientation=landscape"
          alt="AIP Events"
          className="w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/30" />
      </div>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-[#0A1628]/8 blur-3xl" />
        <div className="absolute bottom-1/4 left-1/2 w-56 h-56 rounded-full bg-[#0A1628]/5 blur-2xl" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-40 bg-[#0A1628] rounded-r-full" />
        <div className="absolute top-0 left-0 w-full h-full opacity-5" style={{ backgroundImage: "radial-gradient(circle, #0A1628 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      </div>

      <div className="relative z-10 w-full px-6 lg:px-12 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-120px)]">
          <div className="flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 bg-[#0A1628]/15 border border-[#0A1628]/30 text-[#00C2C2] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6 w-fit">
              <span className="block w-1.5 h-1.5 rounded-full bg-[#0A1628] animate-pulse" />
              {t("eventHeroTag")}
            </div>

            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6">
              {t("eventHeroTitle1")}<br />&amp;{" "}
              <span className="text-[#00C2C2]" style={{ fontStyle: "italic", fontWeight: 400 }}>
                {t("eventHeroTitle2")}
              </span>
            </h1>

            <p className="text-white/60 text-lg leading-relaxed max-w-xl mb-8">
              {t("eventHeroDesc")}
            </p>

            <div className="flex flex-wrap gap-2 mb-10">
              {eventTypes.map((et) => (
                <div key={et.label} className="flex items-center gap-2 bg-white/8 border border-white/12 rounded-full px-4 py-2 text-white/70 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${et.icon} text-[#00C2C2] text-sm`} />
                  </div>
                  {et.label}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mb-12">
              <a
                href="#upcoming"
                className="bg-[#0A1628] text-white text-sm font-bold px-7 py-3.5 rounded-full hover:bg-[#0A1628] transition-all whitespace-nowrap cursor-pointer"
              >
                {t("eventCtaUpcoming")}
              </a>
              <a
                href="#past"
                className="bg-white/10 border border-white/25 text-white text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                {t("eventCtaPast")}
              </a>
            </div>

            <div className="flex flex-wrap gap-6 pt-8 border-t border-white/10">
              {[
                { value: "40+", label: t("eventStat1") },
                { value: "12K+", label: t("eventStat2") },
                { value: "80+", label: t("eventStat3") },
                { value: "15+", label: t("eventStat4") },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                  <div className="text-white/40 text-xs mt-0.5 tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-4">{t("eventFeatured")}</p>
            <div className="bg-white/5 border border-[#0A1628]/30 rounded-2xl overflow-hidden backdrop-blur-sm">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20conference%20hall%20with%20blue%20stage%20lighting%2C%20elegant%20presentation%20setup%2C%20Kigali%20Convention%20Centre%20interior%2C%20luxury%20event%20venue%20with%20dramatic%20lighting%20and%20large%20projection%20screen%2C%20blue%20and%20white%20theme&width=700&height=300&seq=events-feat-card&orientation=landscape"
                  alt="Featured Event"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="bg-[#0A1628] text-white text-xs font-bold px-3 py-1 rounded-full">{featuredEvent.tag}</span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-white font-bold text-xl mb-3">{featuredEvent.title}</h3>

                <div className="space-y-2 mb-5">
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-calendar-line text-[#00C2C2] text-sm" />
                    </div>
                    {featuredEvent.date}
                  </div>
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-2-line text-[#00C2C2] text-sm" />
                    </div>
                    {featuredEvent.location}
                  </div>
                </div>

                <div className="flex gap-4 mb-5">
                  <div className="text-center">
                    <div className="text-white font-bold text-lg">{featuredEvent.speakers}</div>
                    <div className="text-white/40 text-xs">{t("eventSpeakers")}</div>
                  </div>
                  <div className="w-px bg-white/10" />
                  <div className="text-center">
                    <div className="text-white font-bold text-lg">{featuredEvent.attendees}</div>
                    <div className="text-white/40 text-xs">{t("eventExpected")}</div>
                  </div>
                  <div className="w-px bg-white/10" />
                  <div className="text-center">
                    <div className="text-[#00C2C2] font-bold text-lg">{featuredEvent.spots}</div>
                    <div className="text-white/40 text-xs">{t("eventSpotsLeft")}</div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-xs text-white/40 mb-1">
                    <span>{featuredEvent.attendees - featuredEvent.spots} {t("eventRegistered")}</span>
                    <span>{Math.round(((featuredEvent.attendees - featuredEvent.spots) / featuredEvent.attendees) * 100)}% {t("eventFull")}</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full">
                    <div
                      className="h-1.5 bg-[#0A1628] rounded-full"
                      style={{ width: `${Math.round(((featuredEvent.attendees - featuredEvent.spots) / featuredEvent.attendees) * 100)}%` }}
                    />
                  </div>
                </div>

                <a
                  href="#upcoming"
                  className="w-full block text-center bg-[#0A1628] text-white text-sm font-bold px-6 py-3 rounded-full hover:bg-[#0A1628] transition-all whitespace-nowrap cursor-pointer"
                >
                  {t("registerNow")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
